import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ClipboardList, ImageIcon, MapPin, PackageSearch, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RECEIVING_STATUS_META, SESSION_STATUS_META } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ReadyKeepSection } from "@/components/ReadyItems";

const PAGE_ROLES = ["owner", "receiving", "admin", "purchaser", "viewer"];
const CAN_START = ["owner", "receiving"];

const FILTERS = [
  { v: "all", l: "All" },
  { v: "waiting", l: "Waiting to Receive" },
  { v: "partially_received", l: "Partially Received" },
  { v: "fully_received", l: "Fully Received" },
];

function SessionCard({ s, onOpen }) {
  const entered = s.items.filter((it) => it.actual_qty > 0).length;
  return (
    <button
      data-testid={`session-card-${s.id}`}
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
    >
      <ClipboardList className="h-5 w-5 shrink-0 text-stone-400" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{s.batch_name}</p>
        <p className="text-xs text-muted-foreground">
          #{s.id.slice(0, 8)} · {s.pic_name} · {format(new Date(s.created_at), "d MMM, HH:mm")} · {entered}/{s.items.length} products entered
        </p>
      </div>
      <span
        className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${(SESSION_STATUS_META[s.status] || SESSION_STATUS_META.draft).badge}`}
      >
        {(SESSION_STATUS_META[s.status] || SESSION_STATUS_META.draft).label}
      </span>
    </button>
  );
}

export default function Receiving() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startOpen, setStartOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data: queueData } = await api.get("/receiving/queue");
      setData(queueData);
    } catch (e) {
      toast.error(apiError(e));
      setData({ rows: [], summary: null });
    }
    if (user.role !== "viewer") {
      try {
        const { data: sessionData } = await api.get("/receiving/sessions");
        setSessions(sessionData);
      } catch {}
    }
  }, [user.role]);

  const batchesWithWaiting = useMemo(() => {
    const m = new Map();
    for (const r of data?.rows || []) {
      if (r.waiting_qty <= 0) continue;
      const cur = m.get(r.po_batch_id) || { id: r.po_batch_id, name: r.batch_name, units: 0 };
      cur.units += r.waiting_qty;
      m.set(r.po_batch_id, cur);
    }
    return [...m.values()];
  }, [data]);

  const startSession = async (batchId) => {
    setStarting(true);
    try {
      const { data } = await api.post("/receiving/sessions", { po_batch_id: batchId });
      setStartOpen(false);
      navigate(`/receiving/sessions/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setStartOpen(false);
        setDupInfo({ batchId, message: apiError(e) });
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setStarting(false);
    }
  };

  const dupOpenSession =
    dupInfo && sessions.find((s) => s.po_batch_id === dupInfo.batchId && ["draft", "correction_required"].includes(s.status));

  const activeSessions = useMemo(() => sessions.filter((s) => ["draft", "correction_required", "submitted"].includes(s.status)), [sessions]);
  const historySessions = useMemo(() => sessions.filter((s) => !["draft", "correction_required", "submitted"].includes(s.status)), [sessions]);

  useEffect(() => {
    if (PAGE_ROLES.includes(user.role)) load();
  }, [user, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows || []).filter((r) => {
      if (q && !r.product_name.toLowerCase().includes(q) && !r.batch_name.toLowerCase().includes(q)) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      return true;
    });
  }, [data, search, statusFilter]);

  if (!PAGE_ROLES.includes(user.role)) {
    return (
      <div data-testid="receiving-forbidden" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
        <p className="font-medium text-muted-foreground">You don&apos;t have access to Receiving.</p>
      </div>
    );
  }

  return (
    <div data-testid="receiving-page">
      <div className="mb-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Receiving Queue</h1>
            <p className="text-sm text-muted-foreground">Purchased stock waiting to arrive — read directly from purchase records</p>
          </div>
          {CAN_START.includes(user.role) && (
            <Button data-testid="start-session-button" className="h-12 shrink-0 px-4" onClick={() => setStartOpen(true)}>
              <Plus className="h-4 w-4" /> Start Session
            </Button>
          )}
        </div>
        {data?.summary && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="receiving-summary">
            <span className="rounded-md border bg-card px-2.5 py-1 text-xs font-semibold">{data.summary.rows} products</span>
            <span className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
              {data.summary.units_waiting} units waiting
            </span>
            <span className="rounded-md border bg-card px-2.5 py-1 text-xs font-semibold">
              {data.summary.waiting} waiting · {data.summary.partially_received} partial · {data.summary.fully_received} full
            </span>
          </div>
        )}
      </div>

      {CAN_START.includes(user.role) && <ReadyKeepSection />}

      {activeSessions.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="sessions-list">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Active Receiving Sessions</h2>
          {activeSessions.map((s) => (
            <SessionCard key={s.id} s={s} onOpen={() => navigate(`/receiving/sessions/${s.id}`)} />
          ))}
        </div>
      )}

      <div className="mb-3 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input
            data-testid="receiving-search-input"
            className="h-12 pl-9"
            placeholder="Search product or batch…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              data-testid={`receiving-filter-${f.v}`}
              onClick={() => setStatusFilter(f.v)}
              className={`h-9 shrink-0 rounded-full border px-3.5 text-xs font-semibold transition-colors active:scale-[0.98] ${
                statusFilter === f.v ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {data === null ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="receiving-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <div>
            <PackageSearch className="mx-auto mb-3 h-10 w-10 text-stone-300" />
            <p className="font-medium text-muted-foreground">
              {data.rows.length === 0 ? "Nothing purchased yet — the queue fills automatically from purchase records" : "No items match your filters"}
            </p>
          </div>
        </div>
      ) : (
        <div data-testid="receiving-list" className="space-y-2.5">
          {filtered.map((r) => {
            const img = productImage(r);
            const meta = RECEIVING_STATUS_META[r.status];
            return (
              <div key={`${r.po_batch_id}:${r.product_id}`} data-testid={`receiving-row-${r.product_id}`} className="space-y-3 rounded-md border bg-card p-4">
                <div className="flex gap-3">
                  {img ? (
                    <img src={img} alt={r.product_name} className="h-16 w-16 shrink-0 rounded-md border bg-stone-100 object-cover" />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                      <ImageIcon className="h-6 w-6 text-stone-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="truncate font-heading text-base font-bold tracking-tight" data-testid={`receiving-name-${r.product_id}`}>
                      {r.product_name}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-xs font-semibold text-stone-600">
                        {r.batch_name}
                      </span>
                      <span
                        data-testid={`receiving-status-${r.product_id}`}
                        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.locations.length > 0 ? (
                        r.locations.map((l) => (
                          <span key={l} className="inline-flex items-center gap-1 rounded-md border border-rose-100 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
                            <MapPin className="h-3 w-3" /> {l}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">No location recorded</span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div data-testid={`receiving-waiting-${r.product_id}`} className="font-heading text-3xl font-extrabold leading-none text-primary">
                      {r.waiting_qty}
                    </div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Waiting</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md border bg-[#EDE4DE] py-1.5">
                    <p className="text-sm font-bold" data-testid={`receiving-purchased-${r.product_id}`}>{r.total_purchased_qty}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Purchased</p>
                  </div>
                  <div className="rounded-md border bg-[#EDE4DE] py-1.5">
                    <p className="text-sm font-bold" data-testid={`receiving-received-${r.product_id}`}>{r.approved_received_qty}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Received</p>
                  </div>
                  <div className="rounded-md border bg-[#EDE4DE] py-1.5">
                    <p className="text-sm font-bold">{r.waiting_qty}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">To Receive</p>
                  </div>
                </div>
                {CAN_START.includes(user.role) &&
                  (r.waiting_qty > 0 ? (
                    <Button
                      data-testid={`receive-action-${r.product_id}`}
                      className="h-12 w-full text-base"
                      onClick={() => startSession(r.po_batch_id)}
                      disabled={starting}
                    >
                      <Plus className="h-4 w-4" /> Receive {r.waiting_qty} remaining
                    </Button>
                  ) : (
                    <p
                      data-testid={`receive-done-${r.product_id}`}
                      className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-xs font-bold text-emerald-700"
                    >
                      FULLY RECEIVED — nothing left to receive
                    </p>
                  ))}
              </div>
            );
          })}
        </div>
      )}
      {historySessions.length > 0 && (
        <div className="mt-6" data-testid="receiving-history">
          <button
            data-testid="receiving-history-toggle"
            onClick={() => setHistoryOpen(!historyOpen)}
            className="flex h-11 w-full items-center justify-between rounded-md border bg-card px-4 text-sm font-bold text-muted-foreground"
          >
            <span>Receiving History ({historySessions.length})</span>
            <span className="text-xs font-semibold">{historyOpen ? "Hide ▲" : "Show ▼"}</span>
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-2" data-testid="receiving-history-list">
              {historySessions.map((s) => (
                <SessionCard key={s.id} s={s} onOpen={() => navigate(`/receiving/sessions/${s.id}`)} />
              ))}
            </div>
          )}
        </div>
      )}

      <Drawer open={startOpen} onOpenChange={setStartOpen} shouldScaleBackground={false}>        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Start Receiving Session</DrawerTitle>
            <p className="text-sm text-muted-foreground">Choose the PO Batch you are physically receiving</p>
          </DrawerHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto px-4 pb-8" data-testid="start-session-batches">
            {batchesWithWaiting.length === 0 ? (
              <p className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
                No batches with units waiting to receive
              </p>
            ) : (
              batchesWithWaiting.map((b) => (
                <button
                  key={b.id}
                  data-testid={`start-session-batch-${b.id}`}
                  onClick={() => startSession(b.id)}
                  disabled={starting}
                  className="flex w-full items-center justify-between rounded-md border bg-card p-4 text-left transition-colors hover:bg-accent active:scale-[0.99]"
                >
                  <span className="font-heading text-base font-bold">{b.name}</span>
                  <span className="rounded-md bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700">{b.units} units waiting</span>
                </button>
              ))
            )}
          </div>
        </DrawerContent>
      </Drawer>
      <Drawer open={!!dupInfo} onOpenChange={(o) => !o && setDupInfo(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Session already open</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8" data-testid="dup-draft-dialog">
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800" data-testid="dup-draft-message">
              {dupInfo?.message || "An open receiving draft already exists for this batch."}
            </p>
            {dupOpenSession ? (
              <Button
                data-testid="resume-draft-button"
                className="h-12 w-full text-base"
                onClick={() => {
                  setDupInfo(null);
                  navigate(`/receiving/sessions/${dupOpenSession.id}`);
                }}
              >
                Resume Existing Draft
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">The session is waiting for owner approval — no new session can be started yet.</p>
            )}
            <Button variant="outline" data-testid="dup-draft-close" className="h-12 w-full" onClick={() => setDupInfo(null)}>
              Close
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
